import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  Menu,
  ActionIcon,
  Stack,
  Select,
  Pagination,
  Skeleton,
  Tooltip,
  Modal,
  Divider,
} from "@mantine/core";
import {
  IconSearch,
  IconDotsVertical,
  IconEye,
  IconTrash,
  IconRefresh,
  IconUsersGroup,
  IconArrowUp,
  IconArrowDown,
  IconArrowsSort,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import { HttpCodes } from "../../../lib/data";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions } from "../../../lib/data";
import { ISquadProps, squadApiId } from "../../../lib/types/Squad";
import { IUserProps } from "../../../lib/types/User";

type SortColumn = "name" | "membersCount" | "createdAt";
type SortDirection = "asc" | "desc";

const Squads = () => {
  useEffect(() => {
    document.title = "Squads | Virel Admin";
  }, []);

  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortState, setSortState] = useState<{
    column: SortColumn;
    direction: SortDirection;
  }>({ column: "createdAt", direction: "desc" });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [squadToDisband, setSquadToDisband] = useState<ISquadProps | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const squadsEndpoint = useMemo(
    () =>
      Endpoints.GetSquads({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter || undefined,
      }),
    [currentPage, pageSize, debouncedSearch, statusFilter]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<ISquadProps[]>
  >({
    method: squadsEndpoint.method,
    route: squadsEndpoint.route,
    showError: false,
  });

  const toggleSort = (column: SortColumn) => {
    setSortState((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { column, direction: "asc" };
    });
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortState.column !== column) return <IconArrowsSort size={14} />;
    return sortState.direction === "asc" ? (
      <IconArrowUp size={14} />
    ) : (
      <IconArrowDown size={14} />
    );
  };

  const squads = data?.data ?? [];
  const totalRecords =
    data?.totalRecords ??
    (data as { pagination?: { total?: number } } | undefined)?.pagination
      ?.total ??
    0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    totalRecords === 0 ? 0 : Math.min(currentPage * pageSize, totalRecords);

  const formatDate = (value?: string | Date) => {
    if (!value) return "—";
    const date = new Date(value);
    return date.toLocaleDateString();
  };

  const handleDisband = async () => {
    if (!squadToDisband) return;
    const sid = squadApiId(squadToDisband);
    if (!sid) return;
    setIsProcessing(true);
    const endpoint = Endpoints.AdminDisbandSquad(sid);
    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
    });
    setIsProcessing(false);
    if (r.status === HttpCodes.Success || r.status === HttpCodes.NoContent) {
      setConfirmOpen(false);
      setSquadToDisband(null);
      await reloadData();
    }
  };

  const formatNgn = (n: number) =>
    `₦${new Intl.NumberFormat("en-NG", { maximumFractionDigits: 0 }).format(n)}`;

  const rows = squads.map((squad) => {
    const creator = squad.creator as IUserProps;
    const sid = squadApiId(squad);
    return (
      <Table.Tr key={sid ?? squad.inviteCode}>
        <Table.Td>
          <Text fw={500} size="sm">
            {squad.name}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">
            {creator ? `${creator.firstName} ${creator.lastName}` : "Unknown"}
          </Text>
          <Text size="xs" c="dimmed">
            {creator?.email}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" fw={600}>
            {formatNgn((squad as ISquadProps).squadPointsTotal ?? 0)}
          </Text>
          <Text size="xs" c="dimmed">
            epoch {(squad as ISquadProps).squadPointsEpoch ?? 0}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap={6} wrap="nowrap">
            <Badge variant="light" color="blue">
              {squad.membersCount ??
                (squad as { memberCount?: number }).memberCount ??
                0}{" "}
              Members
            </Badge>
            {!!squad.pendingInviteCount && (
              <Badge variant="light" color="orange">
                {squad.pendingInviteCount} Pending
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm" ff="monospace">
            {squad.inviteCode}
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge
            color={squad.status === "active" ? "green" : "red"}
            variant="light"
          >
            {squad.status.toUpperCase()}
          </Badge>
        </Table.Td>
        <Table.Td>{formatDate(squad.createdAt)}</Table.Td>
        <Table.Td>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEye size={14} />}
                onClick={() => {
                  if (!sid) return;
                  navigate(`/d/squads/${sid}`);
                }}
              >
                View Details
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => {
                  setSquadToDisband(squad);
                  setConfirmOpen(true);
                }}
              >
                Disband Squad
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    );
  });

  const skeletonRows = Array.from({ length: 5 }).map((_, idx) => (
    <Table.Tr key={`skeleton-${idx}`}>
      <Table.Td><Skeleton height={20} width="70%" /></Table.Td>
      <Table.Td><Skeleton height={20} width="60%" /></Table.Td>
      <Table.Td><Skeleton height={20} width={72} /></Table.Td>
      <Table.Td><Skeleton height={20} width={80} /></Table.Td>
      <Table.Td><Skeleton height={20} width={60} /></Table.Td>
      <Table.Td><Skeleton height={20} width={70} /></Table.Td>
      <Table.Td><Skeleton height={20} width="50%" /></Table.Td>
      <Table.Td><Skeleton height={24} width={32} /></Table.Td>
    </Table.Tr>
  ));

  return (
    <PermissionGuard
      permission={AdminPermissions.MANAGE_USERS}
      fallback={
        <Container>
          <Text>Unauthorized</Text>
        </Container>
      }
    >
      <div className="squads-page">
        <Container size="xl">
          <Stack gap="lg">
            <Group justify="space-between">
              <div>
                <Title order={1}>Squads</Title>
                <Text size="sm" c="dimmed">
                  Manage and monitor all platform squads
                </Text>
              </div>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => reloadData()}
                loading={isLoading}
              >
                Refresh
              </Button>
            </Group>

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group gap="md" mb="md">
                <TextInput
                  placeholder="Search squads by name or invite code..."
                  leftSection={<IconSearch size={16} />}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  size="md"
                />
                <Select
                  placeholder="Filter by status"
                  data={[
                    { value: "all", label: "All Statuses" },
                    { value: "active", label: "Active" },
                    { value: "disbanded", label: "Disbanded" },
                  ]}
                  value={statusFilter || "all"}
                  onChange={(val) => {
                    setStatusFilter(val === "all" ? null : val);
                    setCurrentPage(1);
                  }}
                  size="md"
                  style={{ width: 160 }}
                />
                <Select
                  data={["10", "20", "50", "100"]}
                  value={String(pageSize)}
                  onChange={(val) => {
                    setPageSize(Number(val));
                    setCurrentPage(1);
                  }}
                  size="md"
                  w={100}
                />
              </Group>

              <div style={{ overflowX: "auto" }}>
                <Table striped highlightOnHover verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th onClick={() => toggleSort("name")} style={{ cursor: "pointer" }}>
                        <Group gap={4}>Name {sortIndicator("name")}</Group>
                      </Table.Th>
                      <Table.Th>Creator</Table.Th>
                      <Table.Th>Squad points</Table.Th>
                      <Table.Th onClick={() => toggleSort("membersCount")} style={{ cursor: "pointer" }}>
                        <Group gap={4}>Members {sortIndicator("membersCount")}</Group>
                      </Table.Th>
                      <Table.Th>Invite Code</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th onClick={() => toggleSort("createdAt")} style={{ cursor: "pointer" }}>
                        <Group gap={4}>Created {sortIndicator("createdAt")}</Group>
                      </Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isLoading ? (
                      skeletonRows
                    ) : rows.length > 0 ? (
                      rows
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={8} style={{ textAlign: "center" }}>
                          <Stack align="center" py="xl">
                            <IconUsersGroup size={48} color="gray" />
                            <Text c="dimmed">No squads found</Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </div>

              <Group justify="space-between" mt="md">
                <Text size="sm" c="dimmed">
                  Showing {rangeStart} to {rangeEnd} of {totalRecords} squads
                </Text>
                {totalPages > 1 && (
                  <Pagination
                    value={currentPage}
                    onChange={setCurrentPage}
                    total={totalPages}
                    size="sm"
                  />
                )}
              </Group>
            </Paper>
          </Stack>
        </Container>

        <Modal
          opened={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          title="Disband Squad"
          centered
        >
          <Stack>
            <Text size="sm">
              Are you sure you want to disband <b>{squadToDisband?.name}</b>? This cannot be undone: all members are removed and the squad&apos;s public squad points reset to zero (epoch advances). Ledger history remains for audit.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button color="red" onClick={handleDisband} loading={isProcessing}>Disband Squad</Button>
            </Group>
          </Stack>
        </Modal>
      </div>
    </PermissionGuard>
  );
};

export default Squads;
